import { ComponentRef, inject, Injectable, RendererFactory2, TemplateRef, Type, ViewContainerRef } from '@angular/core';
import { Router } from '@angular/router';
import { NotifyComponent } from './notify.component';

export interface NotifyOptions {
  icon?: string
  color?: string
  showClose?: boolean
  expire?: number
  copyData?: unknown
}

@Injectable({
  providedIn: 'root'
})
export class NotifyService {
  private notifications: ComponentRef<NotifyComponent>[] = []
  private router = inject(Router)
  private rendererFactory = inject(RendererFactory2)
  private renderer = this.rendererFactory.createRenderer(null, null)
  private tsMap = new Map<string, number>()
  container!: ViewContainerRef

  createFromText(content: string, options?: NotifyOptions) {
    setTimeout(() => {
      const element = this.renderer.createText(content)
      const notifyRef = this.createNotify([[element]])
      if (notifyRef && options) this.setOptions(notifyRef, options)
    })
  }

  createFromHtml(html: string, options?: NotifyOptions) {
    setTimeout(() => {
      const element = this.renderer.createElement('div') as HTMLDivElement
      this.renderer.setProperty(element, 'innerHTML', html)
      element.querySelectorAll('a[routerLink]').forEach(a =>
        this.renderer.listen(a, 'click', () => {
          const url = a.getAttribute('routerLink')
          if (url) {
            this.router.navigateByUrl(url)
          }
        })
      )
      const notifyRef = this.createNotify([Array.from(element.childNodes)])
      if (notifyRef && options) this.setOptions(notifyRef, options)
    })
  }

  createFromTemplate<T = unknown>(template: TemplateRef<T>, context?: T, options?: NotifyOptions) {
    setTimeout(() => {
      const viewRef = template.createEmbeddedView(context ?? {} as T)
      if (context) viewRef.detectChanges()
      const notifyRef = this.createNotify([viewRef.rootNodes])
      if (notifyRef && options) this.setOptions(notifyRef, options)
    })
  }

  createFromNodes(nodes: Node[], options?: NotifyOptions) {
    setTimeout(() => {
      const notifyRef = this.createNotify([nodes])
      if (notifyRef && options) this.setOptions(notifyRef, options)
    })
  }

  createFromComponent<T, C = unknown>(type: Type<T>, data?: C, options?: NotifyOptions) {
    setTimeout(() => {
      const componentRef = this.container.createComponent(type)

      if (data) {
        const instance = componentRef.instance
        Object.entries(data).forEach(([k, v]) => instance[k as keyof T] = v as T[keyof T])
      }

      const notifyRef = this.createNotify([[componentRef.location.nativeElement]])
      if (notifyRef && options) this.setOptions(notifyRef, options)
    })
  }

  private createNotify(nodes: Node[][]): ComponentRef<NotifyComponent>|undefined {
    // собираем внутренний контент уведомления
    let content = ''
    nodes.forEach(arr =>
      arr.forEach(node => {
        if (node instanceof Element) {
          content += node.outerHTML
        } else {
          content += node.textContent || ''
        }
      })
    )
    // время показа такого же уведомления
    const ts = this.tsMap.get(content) || 0, now = Date.now()
    this.tsMap.set(content, now)

    if (ts + 2000 < now) {
      // Ограничение на количество уведомлений
      if (this.notifications.length >= 2) {
        const oldest = this.notifications.shift()
        if (oldest) {
          oldest.destroy()
        }
      }

      const componentRef = this.container.createComponent(NotifyComponent, {projectableNodes: nodes})

      componentRef.instance.expireElement.subscribe(() => {
        componentRef.destroy()
        this.tsMap.delete(content)
        // Удаляем из массива при уничтожении
        this.notifications = this.notifications.filter(n => n !== componentRef)
      })
      componentRef.instance.closeElement.subscribe(() => {
        componentRef.destroy()
        this.tsMap.delete(content)
        // Удаляем из массива при уничтожении
        this.notifications = this.notifications.filter(n => n !== componentRef)
      })

      this.notifications.push(componentRef)  // Сохранение ссылки на уведомление
      return componentRef
    }
    return undefined
  }

  closeAllNotifications() {
    this.notifications.forEach(notifyRef => notifyRef.destroy())
    this.notifications = []
  }

  private setOptions(notifyRef: ComponentRef<NotifyComponent>, options: NotifyOptions) {
    if (options.icon      != undefined) notifyRef.setInput('icon', options.icon)
    if (options.color     != undefined) notifyRef.setInput('color', options.color)
    if (options.showClose != undefined) notifyRef.setInput('showClose', options.showClose)
    if (options.expire    != undefined) notifyRef.setInput('expire', options.expire)
    if (options.copyData  != undefined) notifyRef.setInput('copyData', options.copyData)
  }

}
